import {
    Button,
    Modal,
    ModalActions,
    ModalContent,
    ModalTitle,
    Option,
    SelectField,
    TextField,
    Typography,
} from '@deere/fuel-react';
import { useContext, useState } from 'react';
import styles from '../../pages/Admin/ActiveJobDetails.module.css';
import { DateTime } from 'luxon';
import { useParams } from 'react-router-dom';
import { LoggedInContext } from '../../context/LoggedInContext';
import { errorBannerFromResponse } from '../../utils/errorBanner';

const reasonsForRemoval = [
    'Disciplinary Action',
    'Disqualified from Classification',
    'Disqualified from Department',
    'Disqualified from Job',
    'Failed Required Testing',
    'Medical Restrictions',
    'Other',
];

export const EditEmployeeBidModal = (props) => {
    const [isEditButtonClicked, setIsEditButtonClicked] = useState(false);
    const [reasonForRemoval, setReasonForRemoval] = useState('');
    const [notes, setNotes] = useState('');
    const [isNotesInvalid, setIsNotesInvalid] = useState(false);
    const [isNotesDirty, setIsNotesDirty] = useState(false);
    const [isReasonDirty, setIsReasonDirty] = useState(false);
    const [isReasonInvalid, setIsReasonInvalid] = useState(false);
    const [notesErrorText, setNotesErrorText] = useState('');
    const params = useParams();
    const { makeRequest } = useContext(LoggedInContext);

    const notesHandler = (notesInput) => {
        setIsNotesDirty(true);
        setNotes(notesInput);
        const isNotesInputInvalid = !notesInput;
        setIsNotesInvalid(isNotesInputInvalid);
        if (!isNotesInputInvalid) setNotesErrorText('');
    };

    const reasonHandler = (reasonInput) => {
        setIsReasonDirty(true);
        setReasonForRemoval(reasonInput);
        const isReasonInputInvalid = !reasonInput;
        setIsReasonInvalid(isReasonInputInvalid);
    };

    const closeWithoutCancelOrSubmit = () => {
        setIsEditButtonClicked(false);
    };

    const closeEditEmployeeBidModal = () => {
        setIsEditButtonClicked(false);
        setNotes('');
        setReasonForRemoval('');
    };

    const openEditEmployeeBidModal = () => {
        setIsEditButtonClicked(true);
    };

    const saveHandler = async () => {
        try {
            if (isNotesInvalid || !isNotesDirty) {
                if (!isNotesDirty) {
                    setIsNotesInvalid(true);
                }
                setNotesErrorText(
                    'You must enter notes that explain why you are removing this employee bid'
                );
            } else if (isReasonInvalid || !isReasonDirty) {
                if (!isReasonDirty) setIsReasonInvalid(true);
            } else {
                props.bidderObj.notes = `${reasonForRemoval}: ${notes}`;
                const requestOptions = {
                    method: 'POST',
                    body: JSON.stringify(props.bidderObj),
                };

                const response = await makeRequest(
                    `${process.env.REACT_APP_API_ENDPOINT}/job/${params?.jobNumber}/removeBidder`,
                    requestOptions
                );
                if (response.status === 200) {
                    closeEditEmployeeBidModal();
                    const removalResponse = await response.json();
                    props.refreshActiveList(props.bidderObj.racf);
                    props.refreshOpenings(removalResponse.racf);
                    props.updateJobBidList(removalResponse);
                } else {
                    setNotesErrorText(
                        errorBannerFromResponse(
                            response,
                            'This bid was not removed. Please try again later.'
                        ).message
                    );
                }
            }
        } catch (err) {
            setNotesErrorText(
                errorBannerFromResponse(
                    err,
                    'This bid was not removed. Please try again later.'
                ).message
            );
        }
    };

    return (
        <>
            <Modal
                open={isEditButtonClicked}
                onClose={closeWithoutCancelOrSubmit}
                aria-labelledby="alert-Modal-title"
                aria-describedby="alert-Modal-description"
                maxWidth="xs"
                fullWidth={true}
            >
                <ModalTitle id="alert-Modal-title">
                    Edit Employee Bid
                </ModalTitle>
                <ModalContent>
                    <Typography fontWeight="bold">
                        {props.bidderObj.name}
                    </Typography>
                    <Typography>
                        <b>Seniority Date:</b>{' '}
                        {props.bidderObj.seniorityDate
                            ? DateTime.fromISO(
                                  props.bidderObj.seniorityDate
                              ).toFormat('LL/dd/yy')
                            : ''}{' '}
                        | <b>Department:</b> {props.bidderObj.department}
                    </Typography>
                    <Typography marginBottom={'2em'}>
                        <b>RACF ID:</b> {props.bidderObj.racf}
                    </Typography>
                    <SelectField
                        sx={{ marginBottom: '1em' }}
                        value={reasonForRemoval}
                        onChange={(event) => reasonHandler(event.target.value)}
                        required
                        label="Reason for Removal"
                        error={isReasonInvalid}
                    >
                        <Option disabled hidden value="">
                            Choose a reason
                        </Option>
                        {reasonsForRemoval.map((reason, index) => (
                            <Option key={index} value={reason}>
                                {reason}
                            </Option>
                        ))}
                    </SelectField>
                    <TextField
                        id="notes"
                        required
                        label="Notes"
                        type="text"
                        placeholder={'Notes'}
                        multiline
                        minRows={3}
                        value={notes}
                        onChange={(event) => notesHandler(event.target.value)}
                        helperText={notesErrorText}
                        error={isNotesInvalid}
                    />
                </ModalContent>
                <ModalActions>
                    <Button
                        onClick={closeEditEmployeeBidModal}
                        variant="secondary"
                    >
                        Cancel
                    </Button>
                    <Button onClick={saveHandler} variant="primary">
                        Save Changes
                    </Button>
                </ModalActions>
            </Modal>
            <Button
                variant="secondary"
                className={styles.editEmployeeBidButton}
                onClick={openEditEmployeeBidModal}
            >
                Edit Employee Bid
            </Button>
        </>
    );
};
